import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { IconButton } from '@hivebrite/design-system/Icon';
import { useTheme } from '@hivebrite/design-system/Theme';

import BuilderThemeProvider, {
  VIEW_AS,
} from 'hb-builder/components/common/BuilderThemeProvider';
import Drop from 'hb-builder/components/common/Drop';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { Wrapper, WrapperBuilder } from 'hb-builder/components/common/Wrapper';
import { defaultTabs } from 'hb-builder/constants/tabs';
import {
  useComponentContext,
  useBuilderConfigContext,
  useRendererContext,
} from 'hb-builder/hooks';
import { getJustifyContent } from 'hb-builder/utils/attributes';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { ButtonItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/buttonItemAttributes';
import { BUTTON_MODE_OPTIONS } from 'hb-react/backoffice/types/builder/itemAttributes/buttonItemAttributes';

import ButtonSettings from './ButtonSettings';
import ButtonUI from './components/ButtonUI';

const componentType = 'single';
const DEFAULT_VARIANT = 'primary';

const Button = () => <div />; // never used

const RenderBuilder = () => {
  const { item } = useComponentContext();
  const {
    hivebrite: { networkFont },
  } = useBuilderConfigContext();
  const theme = useTheme();

  const attributes = item?.attributes as ButtonItemAttributes;

  if (attributes?.hidden) {
    return null;
  }

  // we do not want to render the links in the Builder
  const builderAttributes = {
    ...attributes,
    buttonLink: undefined,
  };

  if (attributes?.tied) {
    // A component can be `tied` to another one and so cannot be dragged and dropped.
    return (
      <BuilderThemeProvider viewAs={VIEW_AS.USER}>
        <StyledWrapperBuilder
          $containerPadding={attributes?.containerPadding}
          $fontFamily={
            attributes?.mode === BUTTON_MODE_OPTIONS.NETWORK
              ? networkFont
              : attributes?.buttonFontFamily || networkFont
          }
          $horizontalAlign={attributes?.horizontalAlign}
        >
          <ButtonUI
            attributes={builderAttributes}
            dataTestId="builder-component-button"
            defaultFontFamily={networkFont}
            theme={theme}
          >
            {item.content}
          </ButtonUI>
        </StyledWrapperBuilder>
      </BuilderThemeProvider>
    );
  }

  return (
    <Drop accept={['single']}>
      {() => (
        /* @ts-expect-error untyped */
        <WrapperBuilder attributes={attributes}>
          {({ isDragging }) =>
            isDragging ? null : (
              <StyledWrapperBuilder
                $fontFamily={
                  attributes?.mode === BUTTON_MODE_OPTIONS.NETWORK
                    ? networkFont
                    : attributes?.buttonFontFamily || networkFont
                }
                $horizontalAlign={attributes?.horizontalAlign}
              >
                <BuilderThemeProvider viewAs={VIEW_AS.USER}>
                  <ButtonUI
                    attributes={builderAttributes}
                    dataTestId="builder-component-button"
                    defaultFontFamily={networkFont}
                    theme={theme}
                  >
                    {item.content}
                  </ButtonUI>
                </BuilderThemeProvider>
              </StyledWrapperBuilder>
            )
          }
        </WrapperBuilder>
      )}
    </Drop>
  );
};

const Render = ({ item }: RenderProps<DocumentItem<ButtonItemAttributes>>) => {
  const { isMobilePreview, networkFont } = useRendererContext();
  const theme = useTheme();

  if (!item) {
    return null;
  }

  const { attributes } = item;

  if (attributes?.hidden) {
    return null;
  }

  return (
    /* @ts-expect-error untyped */
    <Wrapper
      attributes={attributes}
      componentType={componentType}
      dataTestId="builder-component-button"
      isMobilePreview={isMobilePreview}
      style={{
        width: '100%',
      }}
    >
      <StyledWrapper
        $fontFamily={attributes?.buttonFontFamily || networkFont}
        $horizontalAlign={attributes?.horizontalAlign}
      >
        <ButtonUI attributes={attributes} theme={theme}>
          {item.content}
        </ButtonUI>
      </StyledWrapper>
    </Wrapper>
  );
};

Button.componentName = 'Button';
Button.componentType = componentType;

Button.componentLabel = __('Button');
Button.componentCategory = 'single';
Button.componentIcon = () => IconButton;

Button.canBeDoubleClicked = true;
Button.canBeDragged = true;
Button.canBeListed = ({ hasDoubleButtons }) => !hasDoubleButtons;
Button.canBeSelected = true;

Button.RenderBuilder = RenderBuilder;
Button.Render = Render;

const defaultConfig = () => ({
  containerPadding: '10px 10px 10px 10px',
  content: __('Click here'),
  horizontalAlign: 'center',
  variant: DEFAULT_VARIANT,
  mode: 'network',
});

Button.item = ({ features }) => {
  const buttonConfig = {
    ...defaultConfig(),
    ...(features?.button || {}),
  };

  return {
    attributes: {
      buttonLink: buttonConfig.link,
      containerPadding: buttonConfig.containerPadding,
      hidden: buttonConfig.hidden,
      horizontalAlign: buttonConfig.horizontalAlign,
      tied: buttonConfig.tied,
      variant: buttonConfig.variant,
      mode: buttonConfig.mode,
      size: buttonConfig.size,
      truncateText: buttonConfig.truncateText,
    },
    category: Button.componentCategory,
    content: buttonConfig.content,
    name: Button.componentName,
    type: Button.componentName,
  };
};

Button.toolbar = ['edit', 'drag', 'duplicate', 'delete'];

Button.Settings = ButtonSettings;
Button.SettingsTabs = defaultTabs;

export default Button;

interface StyledWrapperProps {
  $containerPadding?: string | number;
  $fontFamily?: string | null;
  $horizontalAlign?: string;
}

const StyledWrapperBuilder = styled(Box).attrs(
  ({ $containerPadding = 0, $horizontalAlign }: StyledWrapperProps) => ({
    justifyContent: getJustifyContent($horizontalAlign),
    padding: $containerPadding,
  }),
)<StyledWrapperProps>`
  ${({ $fontFamily }) =>
    $fontFamily && `font-family: ${$fontFamily} !important;`}

  * {
    pointer-events: none;
  }

  button {
    line-height: 1.5;
  }
`;

const StyledWrapper = styled(Box).attrs(
  ({ $horizontalAlign }: StyledWrapperProps) => ({
    justifyContent: getJustifyContent($horizontalAlign),
    width: '100%',
  }),
)<StyledWrapperProps>`
  > div {
    width: 100%;
  }

  a {
    justify-content: ${({ $horizontalAlign }) =>
      getJustifyContent($horizontalAlign)};
    text-decoration: none !important;
    width: 100%;
  }

  button {
    line-height: 1.5;
    ${({ $fontFamily }) =>
      $fontFamily && `font-family: ${$fontFamily} !important;`}
  }
`;
